import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import type { ApplicationConfig } from '@angular/core';
import { provideBrowserGlobalErrorListeners } from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';

import { API_BASE_URL } from '@ebt/shared-api';
import { AuthAwarePreloadStrategy, authInterceptor, provideEbtAuth } from '@ebt/shared-auth';
import { provideEbtIcons } from '@ebt/shared-ui';

import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { notificationRefreshInterceptor } from './shell/notification-refresh.interceptor';

/**
 * Root provider config for the CRM internal app.
 *
 * `withInterceptors` (functional) replaces `withInterceptorsFromDi`
 * here so `authInterceptor` from `shared-auth` plugs straight in. The
 * interceptor attaches the JWT, includes credentials for the refresh
 * cookie, and recovers from 401s via a single refresh.
 *
 * Two router features carry the navigation-feel fixes:
 *
 * - `withPreloading(AuthAwarePreloadStrategy)` warms every lazy page
 *   chunk in the background once the user is signed in, so a nav click
 *   doesn't wait on a network fetch before rendering. Deliberately not
 *   `PreloadAllModules` — that ignores `canMatch` and would pull the
 *   protected chunks down for anonymous visitors, defeating the
 *   `authMatchGuard` placement below.
 * - `withInMemoryScrolling` sends every forward navigation to the top of
 *   the page and restores the previous offset on Back. Without it, going
 *   from a scrolled list into a detail page landed you halfway down the
 *   new page — which reads as a broken render, not a scroll position.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withPreloading(AuthAwarePreloadStrategy),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
    provideHttpClient(
      withFetch(),
      withInterceptors([authInterceptor, notificationRefreshInterceptor]),
    ),
    provideEbtIcons(),
    { provide: API_BASE_URL, useValue: environment.apiBaseUrl },
    provideEbtAuth({
      audience: 'internal',
      loginPath: `${environment.apiBaseUrl}/auth/login`,
      refreshPath: `${environment.apiBaseUrl}/auth/refresh`,
      logoutPath: `${environment.apiBaseUrl}/auth/logout`,
      mePath: `${environment.apiBaseUrl}/auth/me`,
      passwordResetRequestPath: `${environment.apiBaseUrl}/auth/password-reset/request`,
      passwordResetConfirmPath: `${environment.apiBaseUrl}/auth/password-reset/confirm`,
      loginRoute: '/login',
      postLoginRoute: '/',
    }),
  ],
};
